/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import isEmpty from 'lodash.isempty';
import {
    BlacklistValidatorFactory,
    DuplicateValueValidatorFactory,
    ExtensionValidatorFactory,
    RequiredValidatorFactory
} from 'src/core/types/form';

/**
 * A factory to create a required validator. A required validator is simply
 * one that succeeds only if the value given is non empty or whitespace.
 */
export const createRequiredValidator: RequiredValidatorFactory = options => (value: string[]) => {
    if (options && options.deep) {
        return value.every(v => !isEmpty(v.trim()));
    }
    return value.length > 0;
};

/**
 * A factory to create an extension validator. An extension validator will succeed
 * when the given value has the required extension.
 *
 * @param extension A string of the extension required. Ex: 'txt', 'json', etc. The
 * extension should not be prefixed with a '.', only the string part of the extension
 * should be passed.
 */
export const createExtensionValidator: ExtensionValidatorFactory = (extension: string) => (value: string[]) => {
    const normalizedExtension = extension.trim().toLocaleLowerCase();

    return value.every(
        v =>
            v
                .trim()
                .toLocaleLowerCase()
                .split('.')
                .pop() === normalizedExtension
    );
};

/**
 * Creates a validator that validates whether the given value is a duplicate
 * of the original value or not.
 *
 * @param original The original value that is being tested against.
 * @returns A form validator that validates against the original value.
 */
export const createDuplicateValueValidator: DuplicateValueValidatorFactory = (original: string[]) => (value: string[]) => {
    if (original.length !== value.length) {
        return true;
    }

    return original.some((v, i) => v !== value[i]);
};

/**
 * A factory for creating blacklist validators. A black list validator is one
 * that succeeds validation only if the value given does not contain any of the
 * characters defined in the blacklist.
 *
 * @param blacklist A string of the characters to blacklist. The characters should
 * be contiguous with no white spaces between them. The value to validate should not
 * contain any characters in this string.
 */
export const createBlacklistValidator: BlacklistValidatorFactory = ({ mode = 'exclude', characterList = `\\\/\:\?\&\=\*\+\(\)\%\@\$\~\!\#` }) => (
    value: string[]
) =>
    value.every(v => {
        // eslint-disable-next-line security/detect-non-literal-regexp
        const isValid = !new RegExp(`[${characterList}]+`, 'g').test(v.trim());

        return mode === 'exclude' ? isValid : !isValid;
    });
